import { createContext } from 'preact';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { html } from '@shared/utils/rendering/html';
import { AuthConstants } from '../../constants/auth/auth.constants';
import {
    scheduleTokenRefresh,
    clearTokenRefreshSchedule,
} from '../authorisation/token-refresh-scheduler';

// --- Global Singleton ---
const GLOBAL_KEY = '__ERM_PORTAL_AUTH_SINGLETON__';
const singleton = (globalThis[GLOBAL_KEY] ??= {
    Context: createContext(null),
    api: {
        refresh: async () => {
            throw new Error('auth not ready');
        },
        logout: () => {},
    },
});

export const getAuthApi = () => singleton.api;

const AuthContext = singleton.Context;

export const AuthProvider = ({ children }) => {
    const [redirectUrl, setMemoryRedirectUrl] = useState(null);
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);
    const broadcastChannelRef = useRef(null);

    function setRedirectUrl(url) {
        localStorage.setItem('redirectUrl', url);
        setMemoryRedirectUrl(url);
    }

    async function loadUser() {
        try {
            console.log('/api' + AuthConstants.MeEndpoint);
            const res = await fetch('/api' + AuthConstants.MeEndpoint, {
                credentials: 'include',
            });
            if (!res.ok) throw new Error('not-authenticated');
            const me = await res.json();
            AuthConstants.Debug && console.debug('[auth] /auth/me OK', me);
            setUser(me);

            if (me.accessTokenExpiryUtc) {
                scheduleTokenRefresh(me.accessTokenExpiryUtc);
            }
        } catch (e) {
            AuthConstants.Debug && console.debug('[auth] /auth/me NO SESSION', e?.message || e);
            setUser(null);
            clearTokenRefreshSchedule();
        } finally {
            setReady(true);
        }
    }

    async function refresh() {
        AuthConstants.Debug &&
            console.debug('[auth] refresh → POST', '/api' + AuthConstants.RefreshEndpoint);
        const res = await fetch('/api' + AuthConstants.RefreshEndpoint, {
            method: 'POST',
            credentials: 'include',
        });
        if (!res.ok) throw new Error('refresh-failed');

        const data = await res.json();
        AuthConstants.Debug && console.debug('[auth] refresh OK', data);

        if (data.accessTokenExpiryUtc) {
            scheduleTokenRefresh(data.accessTokenExpiryUtc);
        }

        await loadUser();
        broadcastChannelRef.current?.postMessage('login');
        return true;
    }

    function login(returnUrl) {
        const url = new URL(AuthConstants.LoginEndpoint, location.origin);
        if (returnUrl) {
            url.searchParams.set('returnUrl', returnUrl);
            setRedirectUrl(returnUrl);
        }
        AuthConstants.Debug && console.debug('[auth] redirect →', url.toString());
        location.assign(url.toString());
    }

    async function logout() {
        const res = await fetch('/api' + AuthConstants.LogoutEndpoint, {
            method: 'POST',
            credentials: 'include',
        });
        if (!res.ok) throw new Error('not-authenticated');
        setUser(null);
        clearTokenRefreshSchedule();
        broadcastChannelRef.current?.postMessage('logout');
        const returnUrl = location.pathname + location.search + location.hash;
        setRedirectUrl(returnUrl);
        login(returnUrl);
    }

    function redirect() {
        if (isAuthenticated && redirectUrl && redirectUrl != '/') {
            const destination = redirectUrl ?? localStorage.getItem('redirectUrl');
            setRedirectUrl(null);
            return destination;
        }
    }

    // Cross-tab sync
    useEffect(() => {
        broadcastChannelRef.current = new BroadcastChannel('auth');
        broadcastChannelRef.current.onmessage = (e) => {
            if (e.data === 'logout') setUser(null);
            if (e.data === 'login') loadUser();
        };
        return () => broadcastChannelRef.current?.close();
    }, []);

    // Bootstrap once
    useEffect(() => {
        loadUser();
    }, []);

    // Keep bridge up to date
    useEffect(() => {
        singleton.api = { refresh, logout };
    }, [refresh, logout]);

    const isInRole = useCallback(
        (role) => {
            if (Array.isArray(role)) {
                return role.some((r) => user?.roles?.includes(r));
            }
            return user?.roles?.includes(role);
        },
        [user]
    );

    const isAuthenticated = useMemo(() => !!user, [user]);

    const value = useMemo(
        () => ({
            ready,
            user,
            isAuthenticated,
            isInRole,
            login,
            logout,
            refresh,
            redirect,
            // Re-reads the session. The provider only checks it once at app start, so anything that
            // signs the user in without a full page load (the local login form) must call this before
            // navigating, or the route guards still see "not signed in" and bounce back to /Login.
            reloadUser: loadUser,
        }),
        [ready, user, isAuthenticated]
    );

    return html`<${AuthContext.Provider} value=${value}>${children}</${AuthContext.Provider}>`;
};

export const useAuth = () => useContext(AuthContext);
